/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
import * as moment from "moment";
import { Moment } from "moment";
import { PeopleCountManager, PeopleCountResult } from "sh-managers";
import { ScheduleCalendarType, ScheduleCalendarTypes, ShiftTypes } from "sh-models";

import schedulesViewStateStore from "../../schedules/lib/store/store";

import { ScheduleGroupPeopleCountRowContainerProps } from "./ScheduleGroupPeopleCountRowContainerProps";
import { ScheduleGroupPeopleCountRowProps } from "./ScheduleGroupPeopleCountRowProps";

/**
 * Six hours in milliseconds.
 */
const sixHoursInMs = 21600000;

/**
 * The start hours in week view. This defines the starting hours of each interval group cells.
 * In Week view, each interval group cells is made of 24 hours consisting of 4 intervals of 6 hours each.
 * Important: in case of DST time-zone hour changes, impacted interval will not be 6 hours long but adjusted to match the targeted hour (e.g., 12AM, 6AM, 12PM, 6PM).
 */
const weekViewStartHours = 0;

/**
 * The week view morning hours defines until which hour the start hours goes to.
 */
const weekViewMorningHours = 6;

/**
 * The week view afternoon hours defines until which hour the morning hours goes to.
 */
const weekViewAfternoonHours = 12;

/**
 * The week view evening hours defines until which hour the afternoon hours goes to.
 */
const weekViewEveningHours = 18;

/**
 * Gets the 'ScheduleGroupCountRow' component properties based on given schedule group id.
 * @param props The properties.
 * @returns The 'ScheduleGroupCountRow' component properties.
 */
export const getScheduleGroupCountRowProps = (props: ScheduleGroupPeopleCountRowContainerProps): ScheduleGroupPeopleCountRowProps => {
    const { scheduleCalendarType, viewEndDate, viewStartDate } = schedulesViewStateStore();
    const startDate = viewStartDate.clone();
    const startTimestampMs = startDate.valueOf();
    const endDate = viewEndDate.clone();
    const endTimestampMs = endDate.valueOf();
    const peopleCountsByTimestampMs = getPeopleCountsByTimestampMs(props, startTimestampMs, endTimestampMs);

    return {
        cellIntervalMs: sixHoursInMs,
        cellsTimestampsMs: getCellsTimestampsMs(scheduleCalendarType, startDate, endDate),
        endTimestampMs,
        peopleCountsByTimestampMs,
        startTimestampMs
    };
};

/**
 * Gets each cell timestamp in milliseconds. It handles DST time-zone hour changes by respecting the time change and adjusting the cell timestamp accordingly.
 * @param view The schedule view.
 * @param startDate The start date.
 * @param endDate The end date.
 * @returns The cells timestamps in milliseconds.
 */
const getCellsTimestampsMs = (view: ScheduleCalendarType, startDate: Moment, endDate: Moment): number[] => {
    const cellsTimestampsMs: number[] = [];
    const startTimestampMs = startDate.valueOf();
    const endTimestampMs = endDate.valueOf();
    const date = startDate.clone();
    let timestampMs = startTimestampMs;

    while (timestampMs < endTimestampMs) {
        cellsTimestampsMs.push(timestampMs);

        switch (view) {
            case ScheduleCalendarTypes.Week:
                setNextCellDatetimeForWeekView(date);
                break;
            default:
                date.add(1, "day");
                break;
        }

        timestampMs = date.valueOf();
    }

    return cellsTimestampsMs;
};

// Important: the following logic is required because moment date.add(6, "hours") does not respect DST time-zone hour changes.
const setNextCellDatetimeForWeekView = (date: Moment): void => {
    if (date.hours() < weekViewMorningHours) {
        date.hours(weekViewMorningHours);
    } else if (date.hours() < weekViewAfternoonHours) {
        date.hours(weekViewAfternoonHours);
    } else if (date.hours() < weekViewEveningHours) {
        date.hours(weekViewEveningHours);
    } else {
        date.hours(weekViewStartHours);
        date.add(1, "day");
    }
};

const getPeopleCountsByTimestampMs = (
    props: ScheduleGroupPeopleCountRowContainerProps,
    startTimestampMs: number,
    endTimestampMs: number
): PeopleCountResult => {
    const { memberShiftsData } = props;

    const manager = new PeopleCountManager({
        intervalMs: sixHoursInMs,
        limits: {
            endTimestampMs,
            startTimestampMs
        }
    });

    memberShiftsData.forEach(({ member, memberShifts }) => {
        memberShifts.forEach(shift => {
            if (shift.shiftType == ShiftTypes.Working) {
                const endTimestampMs = shift.endTime.valueOf();
                const startTimestampMs = getStartIntervalTimestampMs(shift.startTime);
                const date = moment(startTimestampMs);
                let timestampMs = startTimestampMs;

                while (timestampMs < endTimestampMs) {
                    manager.addTimeEntry({
                        memberId: member.id,
                        shiftHours: 1,
                        timestampMs
                    });

                    setNextCellDatetimeForWeekView(date);
                    timestampMs = date.valueOf();
                }
            }
        });
    });

    return manager.getResult();
};

const getStartIntervalTimestampMs = (startTime: Moment): number => {
    const date = startTime.clone();
    const hours = date.hours();

    if (hours < weekViewMorningHours) {
        date.set("hours", weekViewStartHours);
    } else if (hours < weekViewAfternoonHours) {
        date.set("hours", weekViewMorningHours);
    } else if (hours < weekViewEveningHours) {
        date.set("hours", weekViewAfternoonHours);
    } else {
        date.set("hours", weekViewEveningHours);
    }

    return date.startOf("hours").valueOf();
};
